import { useEffect, useState } from "react";

import { serviceChapters } from "@/config/serviceArchiveData";
import { getVenueXPreviewPayload } from "@/data/previewContent";
import { localizedApiHref } from "@/i18n/locale";
import { localizeContent } from "@/i18n/translations";

export type ServicesArchiveLink = {
  label: string;
  href: string;
};

export type ServicesArchivePresentation = {
  hero_eyebrow: string;
  hero_title: string;
  stats: { label: string; text: string }[];
  services_title: string;
  marquee_words: string[];
  support_title: string;
  support_cards: { title: string; description: string }[];
  detail_eyebrow: string;
  back_label: string;
  cta_eyebrow: string;
  cta_title: string;
  primary_link: ServicesArchiveLink;
  secondary_link: ServicesArchiveLink;
};

export type ServicesArchiveItem = {
  id: number | string;
  slug: string;
  url: string;
  title: string;
  description: string;
  bodyHtml: string;
  featured: boolean;
  note: string;
  accentText: string;
  imageUrl: string;
  imageAlt: string;
  imagePosition: string;
  surfaceColor: string;
  accentColor: string;
};

export type ServicesArchiveData = {
  title: string;
  intro: string;
  seoTitle: string;
  searchDescription: string;
  presentation: ServicesArchivePresentation;
  items: ServicesArchiveItem[];
};

const fallbackStats = [
  "Luxury branding, digital systems, growth support and live production aligned under one standard.",
  "Every service exists to keep the brand coherent before the event, during the event and after the event.",
  "The same authored language carries from identity decisions to on-site experience and follow-through.",
];

export const DEFAULT_SERVICES_ARCHIVE: ServicesArchiveData = {
  title: "What We Do",
  intro: "Branding, digital systems, growth support and live production under one standard.",
  seoTitle: "What We Do",
  searchDescription: "Explore VenueX services across branding, websites, growth systems and event experiences.",
  presentation: {
    hero_eyebrow: "What we do",
    hero_title: "Identity, presence and experience.",
    stats: fallbackStats.map((text) => ({ label: "VenueX service logic", text })),
    services_title: "One studio. One direction.",
    marquee_words: ["branding", "digital", "growth", "experience"],
    support_title: "How the system holds together",
    support_cards: [
      {
        title: "Strategic planning",
        description: "We frame the direction before visuals or logistics begin, so the work stays intentional instead of fragmented.",
      },
      {
        title: "Delivery rhythm",
        description: "Design, content, systems and execution move in the right sequence, which keeps the experience calm and premium.",
      },
      {
        title: "Long-tail value",
        description: "The event is treated as a brand asset that can keep building recognition, memory and trust afterwards.",
      },
    ],
    detail_eyebrow: "Service profile",
    back_label: "Back to services",
    cta_eyebrow: "Next route",
    cta_title: "Ready to define the next brief?",
    primary_link: { label: "Start an inquiry", href: "/contact/" },
    secondary_link: { label: "View portfolio", href: "/portfolio/" },
  },
  items: serviceChapters.map((service, index) => ({
    id: service.id,
    slug: service.href.split("/").filter(Boolean).at(-1) || service.id,
    url: service.href,
    title: service.label,
    description: service.body,
    bodyHtml: "",
    featured: true,
    note: service.note,
    accentText: service.title,
    imageUrl: service.image,
    imageAlt: service.label,
    imagePosition: service.imagePosition,
    surfaceColor: service.surface,
    accentColor: service.accent,
  })),
};

type ServicesArchiveApiResponse = {
  title: string;
  intro: string;
  seo_title: string;
  search_description: string;
  presentation: ServicesArchivePresentation;
  items: Array<{
    id: number;
    slug: string;
    url: string;
    title: string;
    short_description: string;
    body_html: string;
    featured: boolean;
    card_note: string;
    accent_text: string;
    image_url: string;
    image_alt: string;
    image_position: string;
    surface_color: string;
    accent_color: string;
  }>;
};

function mapServicesArchive(response: ServicesArchiveApiResponse): ServicesArchiveData {
  return localizeContent({
    title: response.title,
    intro: response.intro,
    seoTitle: response.seo_title,
    searchDescription: response.search_description,
    presentation: response.presentation,
    items: response.items.map((item) => ({
      id: item.id,
      slug: item.slug,
      url: item.url,
      title: item.title,
      description: item.short_description,
      bodyHtml: item.body_html,
      featured: item.featured,
      note: item.card_note,
      accentText: item.accent_text,
      imageUrl: item.image_url,
      imageAlt: item.image_alt,
      imagePosition: item.image_position,
      surfaceColor: item.surface_color,
      accentColor: item.accent_color,
    })),
  });
}

export function useServicesArchive() {
  const previewContent = getVenueXPreviewPayload<ServicesArchiveApiResponse>("services-archive");
  const [data, setData] = useState(() =>
    previewContent ? mapServicesArchive(previewContent) : localizeContent(DEFAULT_SERVICES_ARCHIVE),
  );

  useEffect(() => {
    if (previewContent) return;

    const controller = new AbortController();
    fetch(localizedApiHref("/api/services/"), {
      signal: controller.signal,
      headers: { Accept: "application/json" },
    })
      .then((response) => {
        if (!response.ok) throw new Error("Services archive unavailable");
        return response.json() as Promise<ServicesArchiveApiResponse>;
      })
      .then((response) => setData(mapServicesArchive(response)))
      .catch((error: unknown) => {
        if (!(error instanceof DOMException && error.name === "AbortError")) {
          setData(localizeContent(DEFAULT_SERVICES_ARCHIVE));
        }
      });

    return () => controller.abort();
  }, [previewContent]);

  return data;
}
